import callAPI from './call-api';
import { List } from 'adapters/list/graphql/fragments/ListFields';
import { listEndpoint as endpoint, language } from 'utils/settings';
import getToken from 'utils/auth';
import { MinimalList, Inspiration } from 'adapters/list/graphqlTypes';
import {
    AddInspirations,
    AddItems,
    CreateList,
    GetLists,
    GetMinimalListsAndInspirationsQuery,
    RemoveInspirations,
    RemoveItems,
    SetSelected,
} from './graphql';

import handleInspirationsRemovedError from './handleInspirationsRemovedError';

export type UnavailableInspirationsRemovedError = {
    code: 'UNAVAILABLE_INSPIRATIONS_REMOVED';
    data: {
        removed_inspirations: string[];
    };
};

type ListError = {
    code: string;
    data?: Object;
};
type ServerResponse<T> = {
    data: T;
    errors?: {
        extensions?: ListError | UnavailableInspirationsRemovedError;
    }[];
};

export const FAVOURITES_IGNORED_ERROR_CODES = ['ITEM_NUMBER_NOT_FOUND'];
const FAVOURITES_GROUPED_ERROR_CODES = [
    'INVALID_ITEM_NUMBER',
    'ILLEGAL_QUANTITY',
];

const _callApi = function <T>(
    query: string,
    variables?: Object
): Promise<ServerResponse<T>> {
    return getToken().then(token =>
        callAPI({ query, variables, token })
            .catch(e => {
                console.error(e);
                return Promise.reject(e);
            })
            .then(response => response.json())
            .then((res: ServerResponse<T>) => {
                function getErrorDetails() {
                    return (res.errors ?? []).map(error => ({
                        code: String(error.extensions?.code),
                        data: error.extensions?.data,
                    }));
                }
                if (res.errors && res.errors.length > 0) {
                    const errorDetails = getErrorDetails();

                    // Deal with specific errors
                    errorDetails.forEach(errorDetail => {
                        switch (errorDetail.code) {
                            case 'UNAVAILABLE_INSPIRATIONS_REMOVED':
                                handleInspirationsRemovedError(
                                    errorDetail as UnavailableInspirationsRemovedError
                                );
                                break;
                            default:
                        }
                    });

                    // Multiple errors
                    if (errorDetails.length > 1) {
                        const errorList = errorDetails
                            .map(({ code }) => code)
                            .join(', ');
                        return Promise.reject(
                            new Error(`Multiple errors: ${errorList}`)
                        );
                    } else {
                        const { code, data } = errorDetails[0];
                        // This error should be grouped. Don't attach the data to the error name.
                        if (FAVOURITES_GROUPED_ERROR_CODES.includes(code)) {
                            return Promise.reject(code);
                        }
                        return Promise.reject(
                            new Error(
                                `Got ${code} when contacting ${endpoint}. Error data: ${JSON.stringify(
                                    data
                                )}`
                            )
                        );
                    }
                }
                return res;
            })
    );
};

export function addItems(
    items: Array<{ itemNo: string; quantity: number }>,
    listId?: string
) {
    return _callApi<{ list: List }>(AddItems(language), {
        items,
        listId,
    });
}

export function removeItem(itemNo: string, listId?: string) {
    return _callApi<{ list: List }>(RemoveItems(language), {
        itemNos: [itemNo],
        listId,
    });
}

export function getLists() {
    return _callApi<{ lists: List[] }>(GetLists(language));
}

export function addInspirations(ids: Array<string>) {
    return _callApi<{ inspirations: Inspiration[] }>(
        AddInspirations(language),
        { ids }
    );
}

export function removeInspirations(ids: Array<string>) {
    return _callApi<{ inspirations: Inspiration[] }>(
        RemoveInspirations(language),
        { ids }
    );
}

export function getMinimalListsAndInspirations() {
    return _callApi<{
        lists: MinimalList[];
        inspirations: Array<Pick<Inspiration, 'id'>>;
    }>(GetMinimalListsAndInspirationsQuery);
}

export function createList(name: string) {
    return _callApi<{ list: List }>(CreateList(language), {
        name,
    });
}
export function setSelected(listId: string) {
    return _callApi<{ list: List[] }>(SetSelected(), {
        listId,
    });
}
